/*
  Brilinton Device Context — R44-291

  Shared, quiet storefront memory for devices a shopper has actively selected.
  No selector is rendered here and nothing is added to the header. UI remains
  owned by the existing collection/PDP selectors.

  Persistence is browser-local only. Account-level persistence belongs to future
  customer metafields / Customer Account work, not to theme-side impersonation.
*/
(() => {
  'use strict';

  const STORAGE_KEY = 'brilinton.device-context.v1';
  const DATA_ID = 'BrilintonDeviceContextData';
  const GLOBAL_KEY = 'BrilintonDeviceContext';
  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/[™®]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const compact = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  const clone = (value) => JSON.parse(JSON.stringify(value || null));

  const parseData = () => {
    const node = document.getElementById(DATA_ID);
    if (!node) return { iphone: [], appleWatch: [], royalPop: [] };
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      return {
        iphone: Array.isArray(parsed.iphone) ? parsed.iphone : [],
        appleWatch: Array.isArray(parsed.appleWatch) ? parsed.appleWatch : [],
        royalPop: Array.isArray(parsed.royalPop) ? parsed.royalPop : [],
      };
    } catch (_) {
      return { iphone: [], appleWatch: [], royalPop: [] };
    }
  };

  const data = parseData();
  let volatileState = { version: 1, iphone: null, appleWatch: null, royalPop: null };

  const readRaw = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return clone(volatileState);
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return clone(volatileState);
      return {
        version: 1,
        iphone: parsed.iphone || null,
        appleWatch: parsed.appleWatch || null,
        royalPop: parsed.royalPop || null,
      };
    } catch (_) {
      return clone(volatileState);
    }
  };

  const writeRaw = (state) => {
    volatileState = {
      version: 1,
      iphone: state?.iphone || null,
      appleWatch: state?.appleWatch || null,
      royalPop: state?.royalPop || null,
    };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(volatileState)); } catch (_) {}
  };

  const findIphone = (value) => {
    if (!value) return null;
    const raw = typeof value === 'object' ? (value.handle || value.name) : value;
    const target = normalize(raw);
    return data.iphone.find((entry) => normalize(entry.handle) === target || normalize(entry.name) === target) || null;
  };

  const findAppleWatch = (value) => {
    if (!value) return null;
    const raw = typeof value === 'object' ? (value.handle || value.size || value.name) : value;
    const target = normalize(raw);
    const targetSize = compact(typeof value === 'object' ? value.size : value);
    return data.appleWatch.find((entry) =>
      normalize(entry.handle) === target
      || normalize(entry.name) === target
      || compact(entry.size) === targetSize
    ) || null;
  };

  const findRoyalPop = (value) => {
    const candidates = typeof value === 'object'
      ? [value.modelSlug, value.handle, value.metaobjectHandle, value.name]
      : [value];
    const targets = candidates.map(normalize).filter(Boolean);
    if (!targets.length) return null;
    return data.royalPop.find((entry) => {
      const aliases = [entry.modelSlug, entry.handle, entry.metaobjectHandle, entry.name]
        .map(normalize)
        .filter(Boolean);
      return targets.some((target) => aliases.includes(target));
    }) || null;
  };

  const royalModelSlug = (entry = get('royalPop')) => {
    if (!entry) return '';
    return entry.modelSlug || entry.handle || normalize(entry.name);
  };

  const validStored = (kind, stored) => {
    if (!stored) return null;
    if (kind === 'iphone') return findIphone(stored) || null;
    if (kind === 'appleWatch') return findAppleWatch(stored) || null;
    if (kind === 'royalPop') return findRoyalPop(stored) || null;
    return null;
  };

  const get = (kind) => {
    const state = readRaw();
    return clone(validStored(kind, state[kind]));
  };

  const snapshot = () => ({
    iphone: get('iphone'),
    appleWatch: get('appleWatch'),
    royalPop: get('royalPop'),
  });

  const store = (kind, entry, source = 'selector') => {
    if (!entry) return null;
    const state = readRaw();
    const next = { ...entry, updatedAt: Date.now(), source };
    state[kind] = next;
    writeRaw(state);
    document.dispatchEvent(new CustomEvent('brilinton:device-context-updated', {
      detail: { kind, value: clone(next), source, snapshot: snapshot() }
    }));
    return clone(next);
  };

  const rememberIphone = (value, source) => store('iphone', findIphone(value), source);
  const rememberAppleWatch = (value, source) => store('appleWatch', findAppleWatch(value), source);
  const rememberRoyalPop = (value, source) => store('royalPop', findRoyalPop(value), source);

  const clear = (kind) => {
    const state = readRaw();
    if (kind && Object.prototype.hasOwnProperty.call(state, kind)) state[kind] = null;
    else {
      state.iphone = null;
      state.appleWatch = null;
      state.royalPop = null;
    }
    writeRaw(state);
  };

  const iphoneVariantValue = (entry = get('iphone')) => {
    if (!entry) return '';
    const generation = String(entry.generation || '').replace(/^\s*iPhone\s+/i, '').trim();
    const model = String(entry.model || '').trim();
    return [generation, model].filter(Boolean).join(' ');
  };

  const exactOption = (root, value) => {
    if (!root || !value) return null;
    const target = String(value).trim().toLowerCase();
    const radios = Array.from(root.querySelectorAll('input[type="radio"][value]'));
    const radio = radios.find((input) => String(input.value || '').trim().toLowerCase() === target);
    if (radio) return { kind: 'radio', node: radio };
    const selects = Array.from(root.querySelectorAll('select'));
    for (const select of selects) {
      const option = Array.from(select.options || []).find((item) => String(item.value || '').trim().toLowerCase() === target);
      if (option) return { kind: 'select', node: select, value: option.value };
    }
    return null;
  };

  const currentProductTitle = () => {
    const contextNode = document.querySelector('[data-br-product-context-data]');
    try { return JSON.parse(contextNode?.textContent || '{}').productTitle || ''; } catch (_) { return ''; }
  };

  const addContextParam = (key, value) => {
    if (!key || !value) return;
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    history.replaceState(history.state || {}, '', `${url.pathname}${url.search}${url.hash}`);
  };



  // Apple Watch memory can personalize an intentional internal category click,
  // but must never falsify the broad /apple-watch-cases page itself.
  // Keep crawlable hrefs canonical in markup; only a normal same-tab shopper
  // click is upgraded to the remembered canonical size collection.
  const APPLE_CONFIRM_SESSION_KEY = 'brilinton.apple-watch.confirm-selector.v1';

  const appleWatchCollectionPath = (entry = get('appleWatch')) => {
    const size = compact(entry?.size || '');
    return size ? `/collections/apple-watch-${size}` : '';
  };

  const markAppleWatchConfirmationIntent = (entry) => {
    if (!entry?.size) return;
    try {
      window.sessionStorage.setItem(APPLE_CONFIRM_SESSION_KEY, JSON.stringify({
        size: entry.size,
        at: Date.now()
      }));
    } catch (_) {}
  };

  const maybePersonalizeAppleWatchCategoryClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target?.closest?.('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

    let target;
    try { target = new URL(link.href, window.location.origin); } catch (_) { return; }
    if (target.origin !== window.location.origin) return;
    if (target.pathname.replace(/\/$/, '') !== '/collections/apple-watch-cases') return;

    // A selector/edition "All" control is an explicit request for the broad page.
    // Never reinterpret that local collection-navigation action from memory.
    if (link.closest('[data-collection-context-root]')) return;

    // If the shopper is already on the broad category, a same-page category link
    // is not a reason to bounce them into a remembered size collection.
    if (window.location.pathname.replace(/\/$/, '') === '/collections/apple-watch-cases') return;

    const remembered = get('appleWatch');
    const destinationPath = appleWatchCollectionPath(remembered);
    if (!remembered || !destinationPath) return;

    event.preventDefault();
    markAppleWatchConfirmationIntent(remembered);
    target.pathname = destinationPath;
    target.searchParams.delete('size');
    window.location.assign(`${target.pathname}${target.search}${target.hash}`);
  };

  const applyRememberedProductContext = () => {
    if (!/\/products\//.test(window.location.pathname)) return;
    const url = new URL(window.location.href);

    // Explicit links always win. Collection-card variant continuity from R44-284
    // is therefore never replaced by browser memory.
    if (!url.searchParams.has('variant') && url.searchParams.get('pdp_select') !== '1') {
      const explicitIphone = findIphone(url.searchParams.get('iphone_device'));
      const iphone = explicitIphone || get('iphone');
      const desired = iphoneVariantValue(iphone);
      const picker = exactOption(document.querySelector('variant-selects'), desired);
      if (iphone && picker) {
        if (!url.searchParams.has('iphone_device')) {
          url.searchParams.set('iphone_device', iphone.handle);
          history.replaceState(history.state || {}, '', `${url.pathname}${url.search}${url.hash}`);
        }
        if (picker.kind === 'radio') {
          if (!picker.node.checked) {
            picker.node.checked = true;
            picker.node.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (picker.kind === 'select' && picker.node.value !== picker.value) {
          picker.node.value = picker.value;
          picker.node.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Royal Pop PDPs already own their composition via the existing model event.
    // Restore only when the URL does not explicitly name a model.
    if (!url.searchParams.has('model') && document.querySelector('[data-royal-pop-model-choice]')) {
      const royal = get('royalPop');
      if (royal) {
        const royalSlug = royalModelSlug(royal);
        if (royalSlug) {
          const next = new URL(window.location.href);
          next.searchParams.set('model', royalSlug);
          history.replaceState(history.state || {}, '', `${next.pathname}${next.search}${next.hash}`);
          document.dispatchEvent(new CustomEvent('royal-pop:model-change', {
            detail: { modelSlug: royalSlug, source: 'device-context-restore' }
          }));
        }
      }
    }

    // Apple Watch WatchWare PDPs can safely restore an exact size only when that
    // exact option exists on the current product. Do not infer a size on products
    // that do not actually expose it.
    const productTitle = currentProductTitle();
    const looksAppleWatch = /apple\s*watch/i.test(productTitle) || (/watchware/i.test(productTitle) && !/royal\s*pop/i.test(productTitle));
    if (looksAppleWatch && !url.searchParams.has('variant') && url.searchParams.get('pdp_select') !== '1') {
      const explicitApple = findAppleWatch(url.searchParams.get('device') || url.searchParams.get('size'));
      const apple = explicitApple || get('appleWatch');
      const picker = exactOption(document.querySelector('variant-selects'), apple?.size || '');
      if (apple && picker) {
        if (!url.searchParams.has('size') && !url.searchParams.has('device')) {
          const next = new URL(window.location.href);
          next.searchParams.set('size', apple.size);
          history.replaceState(history.state || {}, '', `${next.pathname}${next.search}${next.hash}`);
        }
        if (picker.kind === 'radio') {
          if (!picker.node.checked) {
            picker.node.checked = true;
            picker.node.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (picker.kind === 'select' && picker.node.value !== picker.value) {
          picker.node.value = picker.value;
          picker.node.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  };

  // If a shopper manually changes an iPhone model in the native Shopify variant
  // picker, that is also an explicit device statement and should become memory.
  document.addEventListener('change', (event) => {
    if (!event.target?.closest?.('variant-selects')) return;
    const value = String(event.target.value || '').trim();
    if (!value) return;

    const iphone = data.iphone.find((entry) => iphoneVariantValue(entry).toLowerCase() === value.toLowerCase());
    if (iphone) {
      rememberIphone(iphone, 'product-variant-selector');
      addContextParam('iphone_device', iphone.handle);
      return;
    }

    const productTitle = currentProductTitle();
    const looksAppleWatch = /apple\s*watch/i.test(productTitle) || (/watchware/i.test(productTitle) && !/royal\s*pop/i.test(productTitle));
    if (!looksAppleWatch) return;
    const apple = data.appleWatch.find((entry) => compact(entry.size) === compact(value));
    if (apple) {
      rememberAppleWatch(apple, 'product-variant-selector');
      addContextParam('size', apple.size);
    }
  });

  window[GLOBAL_KEY] = {
    version: 1,
    data: () => clone(data),
    get,
    snapshot,
    rememberIphone,
    rememberAppleWatch,
    rememberRoyalPop,
    royalModelSlug,
    clear,
    iphoneVariantValue,
    appleWatchCollectionPath,
    appleWatchConfirmationSessionKey: APPLE_CONFIRM_SESSION_KEY,
  };

  document.addEventListener('click', maybePersonalizeAppleWatchCategoryClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.requestAnimationFrame(applyRememberedProductContext), { once: true });
  } else {
    window.requestAnimationFrame(applyRememberedProductContext);
  }
})();
